/**
 * atom-ga4-ecommerce-events.js
 * ----------------------------
 * Theme-level GA4 ecommerce instrumentation for the three events the
 * Customer Events pixel cannot fire from its sandbox:
 *
 *   - view_item       (PDP load + variant change)
 *   - add_to_cart     (successful AJAX add)
 *   - begin_checkout  (checkout button click — cart page + cart drawer)
 *
 * Transport: main-window gtag (injected by the inline atom-ga4-pageview
 * block in theme.liquid). Uses the same safe-queue pattern as
 * atom-ga4-gap-events.js.
 *
 * NEVER fire from this file:
 *   page_view, view_item_list, view_cart — handled by atom-ga4-gap-events.js
 *   purchase, add_payment_info          — handled by the Web Pixel
 *
 * All events include  send_to: GA4_ID  to prevent fan-out to the
 * Google Ads tag (AW-476970591).
 */
(function () {
  'use strict';

  /* Double-run guard */
  if (window.__atomGa4EcomEvents) return;
  window.__atomGa4EcomEvents = true;

  /* ── gtag safe queue ─────────────────────────────────────── */
  var GA4_ID = 'G-0MDTFMBPCG';

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }

  /* ── helpers ─────────────────────────────────────────────── */

  /** Shopify prices are integers in the shop's minor unit (pence for GBP).
   *  GA4 expects a decimal float. */
  function penniesToDecimal(p) {
    return Math.round(p) / 100;
  }

  /** Build a single GA4 item object from available data. */
  function buildItem(opts) {
    var item = {
      item_id: opts.sku || String(opts.variantId || ''),
      item_name: opts.title || '',
      price: penniesToDecimal(opts.pricePennies || 0),
      quantity: opts.quantity || 1
    };
    if (opts.brand) item.item_brand = opts.brand;
    if (opts.variantTitle && opts.variantTitle !== 'Default Title') {
      item.item_variant = opts.variantTitle;
    }
    if (opts.category) item.item_category = opts.category;
    return item;
  }

  /* ── Product data injected by Liquid ─────────────────────── */

  /** Read the JSON data block injected by atom-product-main.liquid.
   *  Returns null on non-product pages or if the block is missing. */
  function getProductData() {
    var el = document.querySelector('script[data-atom-ga4-product]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /** Resolve the currently selected variant from __apdpVariants__ + hidden input. */
  function getCurrentVariant(productData) {
    var idInput = document.getElementById('apdpVariantId');
    if (!idInput) return null;
    var selectedId = parseInt(idInput.value, 10);
    var variants = window.__apdpVariants__ || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].id === selectedId) return variants[i];
    }
    // Fallback: return initial variant from the product data block
    if (productData && productData.initialVariant) return productData.initialVariant;
    return null;
  }

  /** Build the variant title from options array (matches Shopify's "X / Y" format). */
  function variantTitle(variant) {
    if (!variant || !variant.options) return '';
    return variant.options.join(' / ');
  }

  /* ══════════════════════════════════════════════════════════
   *  EVENT 1: view_item
   * ══════════════════════════════════════════════════════════ */

  var lastViewItemVariantId = null;

  function fireViewItem() {
    var pd = getProductData();
    if (!pd) return;

    var variant = getCurrentVariant(pd);
    if (!variant) return;

    // Dedupe: don't re-fire for the same variant (e.g. init + immediate change to same)
    if (variant.id === lastViewItemVariantId) return;
    lastViewItemVariantId = variant.id;

    var item = buildItem({
      sku: variant.sku,
      variantId: variant.id,
      title: pd.title,
      brand: pd.vendor,
      category: pd.type,
      pricePennies: variant.price,
      variantTitle: variantTitle(variant),
      quantity: 1
    });

    gtag('event', 'view_item', {
      currency: 'GBP',
      value: item.price,
      items: [item],
      send_to: GA4_ID
    });
  }

  /* Hook variant changes. The flow is:
   *   pill click -> apdpSelectVariantPill -> sets hidden <select> value
   *   -> dispatches 'change' event (bubbles) -> apdpUpdateVariant runs
   *   (updates #apdpVariantId.value synchronously)
   *
   * We listen for 'change' on #apdp-root, filter for .buybox-select targets,
   * then fire view_item after a microtask so #apdpVariantId is already updated. */
  function observeVariantChanges() {
    var root = document.getElementById('apdp-root');
    if (!root) return;

    root.addEventListener('change', function (e) {
      var target = e.target;
      if (target && target.classList && target.classList.contains('buybox-select')) {
        // Microtask: apdpUpdateVariant runs synchronously in the same event
        // loop turn (it's the onchange handler), so #apdpVariantId is updated
        // by the time this microtask executes.
        Promise.resolve().then(fireViewItem);
      }
    });
  }

  /* ══════════════════════════════════════════════════════════
   *  EVENT 2: add_to_cart
   * ══════════════════════════════════════════════════════════ */

  /** Fire add_to_cart. Called from the PDP ATC success path.
   *  Reads from the product data block + the variant/qty at time of add. */
  function fireAddToCart(variantId, quantity) {
    var pd = getProductData();
    if (!pd) return;

    var variants = window.__apdpVariants__ || [];
    var variant = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].id === variantId) { variant = variants[i]; break; }
    }
    if (!variant) return;

    var item = buildItem({
      sku: variant.sku,
      variantId: variant.id,
      title: pd.title,
      brand: pd.vendor,
      category: pd.type,
      pricePennies: variant.price,
      variantTitle: variantTitle(variant),
      quantity: quantity
    });

    gtag('event', 'add_to_cart', {
      currency: 'GBP',
      value: item.price * quantity,
      items: [item],
      send_to: GA4_ID
    });
  }

  /* Expose a global hook that the ATC handler in atom-product-main.liquid
   * calls on success. This avoids monkey-patching fetch or duplicating the
   * ATC logic. The PDP inline script calls:
   *   window.__atomGa4AddToCart(variantId, quantity)
   * after the /cart/add.js fetch resolves successfully. */
  window.__atomGa4AddToCart = function (variantId, quantity) {
    fireAddToCart(parseInt(variantId, 10), parseInt(quantity, 10) || 1);
  };

  /* ══════════════════════════════════════════════════════════
   *  EVENT 3: begin_checkout
   *
   *  All checkout buttons are <button type="submit" name="checkout"
   *  value="checkout"> that submit a <form action="/cart" method="post">.
   *  Shopify interprets the checkout=checkout param as a redirect to
   *  /checkout. Without preventDefault the form submits immediately
   *  and the async fetch('/cart.js') + gtag call are aborted.
   *
   *  Pattern: intercept click -> preventDefault -> fetch cart ->
   *  gtag with event_callback -> programmatic form submit.
   *  Safety timeout (800ms) guarantees navigation even if GA4 or
   *  /cart.js is slow/fails. Second rapid click falls through to
   *  native submit so the user is never trapped.
   * ══════════════════════════════════════════════════════════ */

  var checkoutInFlight = false;

  /** Programmatically submit a checkout form.
   *  form.submit() skips the submit button's name/value, so we inject
   *  a hidden input to carry checkout=checkout that Shopify requires. */
  function submitCheckoutForm(form) {
    if (!form) {
      // Last-resort fallback: direct navigation
      window.location.href = '/checkout';
      return;
    }
    // Ensure the checkout param is present even without the button
    var hidden = form.querySelector('input[name="checkout"]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = 'checkout';
      form.appendChild(hidden);
    }
    form.submit();
  }

  /** Resolve the form that a checkout button submits to.
   *  Buttons use the `form` attribute to reference a form by ID
   *  (e.g. form="cart" or form="CartDrawer-Form"), or fall back
   *  to the nearest ancestor form. */
  function getCheckoutForm(btn) {
    var formId = btn.getAttribute('form');
    if (formId) {
      var f = document.getElementById(formId);
      if (f) return f;
    }
    return btn.closest('form');
  }

  /** Fire begin_checkout, then call `done` when the event is sent
   *  (or after a safety timeout). */
  function fireBeginCheckout(done) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) {
          done();
          return;
        }

        var items = cart.items.map(function (it, i) {
          return {
            item_id: it.sku || String(it.variant_id),
            item_name: it.product_title,
            price: it.price / 100,
            quantity: it.quantity,
            index: i,
            item_brand: it.vendor || 'ATOM LED',
            item_variant: it.variant_title && it.variant_title !== 'Default Title'
              ? it.variant_title : undefined
          };
        });

        gtag('event', 'begin_checkout', {
          currency: 'GBP',
          value: cart.total_price / 100,
          items: items,
          send_to: GA4_ID,
          event_callback: function () { done(); }
        });
      })
      .catch(function () {
        // /cart.js failed — navigate anyway, never block checkout
        done();
      });
  }

  /** Click handler for all checkout buttons.
   *  First click: preventDefault, fire GA4, then navigate.
   *  Second rapid click (within 2s debounce): fall through to
   *  native form submission so the user is never trapped. */
  function handleCheckoutClick(e) {
    // If a checkout is already in flight (debounce window), let the
    // native submit proceed — never trap the user.
    if (checkoutInFlight) return;

    e.preventDefault();
    checkoutInFlight = true;

    var btn = e.currentTarget;
    var form = getCheckoutForm(btn);

    // Once-only navigation guard — event_callback OR timeout, not both
    var navigated = false;
    function navigate() {
      if (navigated) return;
      navigated = true;
      checkoutInFlight = false;
      submitCheckoutForm(form);
    }

    // Safety timeout: navigate after 800ms regardless of GA4/fetch
    setTimeout(navigate, 800);

    // Fire the event; navigate on callback
    fireBeginCheckout(navigate);
  }

  function bindCheckoutButtons() {
    // Cart page: buttons with data-xct-checkout (2 on the cart page)
    document.querySelectorAll('[data-xct-checkout]').forEach(function (btn) {
      btn.addEventListener('click', handleCheckoutClick);
    });

    // Cart drawer: button with data-cart-drawer-checkout
    document.querySelectorAll('[data-cart-drawer-checkout]').forEach(function (btn) {
      btn.addEventListener('click', handleCheckoutClick);
    });
  }

  /* Cart drawer is replaced after every AJAX add (replaceWith in ATC handler).
   * Re-bind the checkout button after drawer DOM swaps. We observe the
   * cart-drawer custom element for childList mutations. */
  function observeDrawerRebind() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var observer = new MutationObserver(function () {
      // Re-bind after a small delay so the new DOM settles
      setTimeout(function () {
        var btn = drawer.querySelector('[data-cart-drawer-checkout]');
        if (btn && !btn.__atomGa4Bound) {
          btn.__atomGa4Bound = true;
          btn.addEventListener('click', handleCheckoutClick);
        }
      }, 100);
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }

  /* ── init ────────────────────────────────────────────────── */
  function init() {
    // view_item: fire on PDP load
    fireViewItem();
    observeVariantChanges();

    // begin_checkout: bind checkout buttons
    bindCheckoutButtons();
    observeDrawerRebind();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
